/** One shared notification inbox (#344): top-bar panel, Home preview, and the filterable full screen. */
import { Button, Chip, ErrorBanner, Input, Picker, Popover, PopoverContent, PopoverTrigger, Section, SkeletonRows } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Bell } from 'lucide-react'
import * as React from 'react'
import { Frame, PaneHeader } from '../../components/frame'
import { describeError } from '../../lib/api/client'
import type { NotificationOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { useMe } from '../auth/session'
import { CliqUnroutedInbox } from '../cliq/unrouted'
import { SentMailUnroutedCard } from '../sent-mail/unrouted-card'
import {
  inboundCaseSearchQuery,
  inboundThreadsQuery,
  type NotificationCategory,
  notificationsFeedQuery,
  type UnroutedInboundOut,
  unroutedInboundQuery,
  useReadAllNotifications,
  useReadNotification,
  useRouteInbound,
  useSnoozeNotification,
} from './api'

function when(iso: string): string {
  const value = new Date(iso)
  return Number.isNaN(value.getTime()) ? '' : formatWhen(value.toISOString(), { style: 'absolute' })
}

function place(n: NotificationOut): string | null {
  const bits = [n.case_number, n.ticket_number ? `Desk #${n.ticket_number}` : null].filter(Boolean)
  return bits.length ? bits.join(' · ') : null
}

function tomorrow(): string {
  const value = new Date()
  value.setDate(value.getDate() + 1)
  value.setHours(8, 0, 0, 0)
  return value.toISOString()
}

export function InboxRow({ notification: n, allowSnooze = false }: { notification: NotificationOut; allowSnooze?: boolean }) {
  const read = useReadNotification()
  const snooze = useSnoozeNotification()
  const destination = n.href
  const open = async (event: React.MouseEvent<HTMLAnchorElement>, href: string | null | undefined) => {
    if (!href || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    try {
      await read.mutateAsync(n.id)
    } finally {
      window.location.assign(href)
    }
  }
  return (
    <li data-notification-id={n.id} className={`space-y-1 px-3.5 py-3 ${n.read_at ? '' : 'border-l-2 border-l-primary bg-primary/5'}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <Chip size="sm" tone={n.category === 'needs_action' ? 'warning' : n.category === 'mention' ? 'primary' : 'neutral'}>
              {n.category === 'needs_action' ? 'Needs action' : n.category === 'mention' ? 'Mention' : 'FYI'}
            </Chip>
            {place(n) ? <span>{place(n)}</span> : null}
            <time className="ml-auto tabular">{when(n.at)}</time>
          </p>
          <p className="mt-1 font-medium">{n.title}</p>
          {n.body ? <p className="line-clamp-3 whitespace-pre-wrap text-sm text-muted-foreground">{n.body}</p> : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {destination && !n.actions ? (
          <a
            href={destination}
            onClick={(event) => void open(event, destination)}
            className="text-sm font-medium text-primary hover:underline"
          >
            {n.action_label || 'Open item'}
          </a>
        ) : null}
        {n.actions ? (
          <>
            <a
              href={n.actions.set_next_move}
              onClick={(event) => void open(event, n.actions?.set_next_move)}
              className="text-sm font-medium text-primary hover:underline"
            >
              Set next move
            </a>
            <a
              href={n.actions.take_ownership}
              onClick={(event) => void open(event, n.actions?.take_ownership)}
              className="text-sm font-medium text-primary hover:underline"
            >
              Take ownership
            </a>
            <a
              href={n.actions.reply}
              onClick={(event) => void open(event, n.actions?.reply)}
              className="text-sm font-medium text-primary hover:underline"
            >
              Reply
            </a>
            <a
              href={n.actions.snooze}
              onClick={(event) => void open(event, n.actions?.snooze)}
              className="text-sm font-medium text-primary hover:underline"
            >
              Snooze
            </a>
          </>
        ) : null}
        {!n.read_at ? (
          <Button size="sm" variant="ghost" disabled={read.isPending} onClick={() => read.mutate(n.id)}>
            Mark read
          </Button>
        ) : null}
        {allowSnooze ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={snooze.isPending}
            onClick={() => snooze.mutate({ id: n.id, until: n.snoozed_until ? null : tomorrow() })}
          >
            {n.snoozed_until ? 'Unsnooze' : 'Snooze until tomorrow'}
          </Button>
        ) : null}
      </div>
    </li>
  )
}

function InboxList({ items, allowSnooze = false }: { items: NotificationOut[]; allowSnooze?: boolean }) {
  return (
    <ul className="divide-y divide-border" data-testid="notifications">
      {items.map((notification) => (
        <InboxRow key={notification.id} notification={notification} allowSnooze={allowSnooze} />
      ))}
    </ul>
  )
}

export function NotificationsBell() {
  const query = useQuery(notificationsFeedQuery({ limit: 5 }))
  const unread = query.data?.unread ?? 0
  const label = query.isError ? 'Notifications (count unreachable)' : `${unread} unread notification${unread === 1 ? '' : 's'}`
  return (
    <Popover>
      <PopoverTrigger
        aria-label={label}
        title={label}
        data-slot="shell-icon"
        className="relative inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Bell className="size-4" />
        {unread > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-destructive px-1 text-center text-2xs font-semibold leading-4 text-white tabular">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(26rem,calc(100vw-1rem))] p-0" aria-label="Notifications">
        <div className="flex items-center justify-between border-b border-border px-3.5 py-2.5">
          <p className="font-semibold">Notifications</p>
          <span className="text-xs text-muted-foreground">{query.isError ? 'count unknown' : `${unread} unread`}</span>
        </div>
        {query.isPending ? <SkeletonRows count={3} /> : null}
        {query.isError ? <p className="p-3 text-sm text-warning">Inbox unreachable. Nothing here would mean unknown.</p> : null}
        {query.data?.items.length ? <InboxList items={query.data.items} /> : null}
        {query.data && query.data.items.length === 0 ? <p className="p-3 text-sm text-muted-foreground">No active notifications.</p> : null}
        <Link to="/notifications" className="block border-t border-border px-3.5 py-2.5 text-center text-sm font-medium text-primary">
          View all notifications
        </Link>
      </PopoverContent>
    </Popover>
  )
}

export function NotificationsSection() {
  const query = useQuery(notificationsFeedQuery({ limit: 8 }))
  return (
    <Section title="Notifications" icon={<Bell />} count={query.data?.unread} bodyClassName="p-0">
      {query.isPending ? <SkeletonRows count={2} /> : null}
      {query.isError ? (
        <ErrorBanner
          title="Couldn't load notifications"
          detail="Nothing here means unknown, not empty."
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {query.data?.items.length ? <InboxList items={query.data.items} /> : null}
      {query.data && query.data.items.length === 0 ? (
        <p className="px-3.5 py-3 text-sm text-muted-foreground">No active notifications.</p>
      ) : null}
      <Link to="/notifications" className="block border-t border-border px-3.5 py-2 text-sm font-medium text-primary">
        View all notifications
      </Link>
    </Section>
  )
}

const FILTERS: { label: string; value?: NotificationCategory }[] = [
  { label: 'All' },
  { label: 'Mentions', value: 'mention' },
  { label: 'Needs action', value: 'needs_action' },
  { label: 'FYI', value: 'fyi' },
]

function routeLabel(audience: string, name?: string | null): string {
  const pane = audience === 'internal' ? 'Internal' : audience.charAt(0).toUpperCase() + audience.slice(1)
  return name ? `${pane} · ${name}` : pane
}

export function UnroutedCard({ message }: { message: UnroutedInboundOut }) {
  if (message.kind === 'sent_mail') return <SentMailUnroutedCard message={message} />
  return <ReplyUnroutedCard message={message} />
}

function ReplyUnroutedCard({ message }: { message: UnroutedInboundOut }) {
  const initialCase = message.suggested_case_ids?.[0] ?? null
  const [caseId, setCaseId] = React.useState<string | null>(initialCase)
  const [caseLabel, setCaseLabel] = React.useState(initialCase ? `Suggested case ${initialCase}` : '')
  const [search, setSearch] = React.useState('')
  const [threadId, setThreadId] = React.useState('')
  const searchQuery = useQuery(inboundCaseSearchQuery(search.trim()))
  const threadsQuery = useQuery(inboundThreadsQuery(caseId))
  const route = useRouteInbound()
  const threads = threadsQuery.data ?? []

  React.useEffect(() => {
    if (threads.length && !threads.some((thread) => thread.id === threadId)) {
      const preferred = message.audience ? threads.find((thread) => thread.audience === message.audience) : null
      const next = preferred ?? threads[0]
      if (next) setThreadId(next.id)
    }
  }, [message.audience, threadId, threads])

  return (
    <article
      id={`inbound-${message.id}`}
      className={`space-y-3 rounded-lg border bg-card p-4 ${new URLSearchParams(window.location.search).get('inbound') === message.id ? 'border-primary ring-1 ring-primary' : 'border-border'}`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{message.subject || '(no subject)'}</p>
          <p className="text-sm text-muted-foreground">
            {message.from_name || message.from_addr || 'Unknown sender'} · {when(message.received_at)}
          </p>
        </div>
        <Chip size="sm" tone="warning">
          {message.route_reason?.replaceAll('_', ' ') || 'Needs routing'}
        </Chip>
      </div>
      {message.excerpt ? <p className="line-clamp-4 whitespace-pre-wrap text-sm">{message.excerpt}</p> : null}
      {message.attachments?.length ? (
        <p className="text-xs text-muted-foreground">
          {message.attachments.length} attachment{message.attachments.length === 1 ? '' : 's'} recorded
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <div className="relative">
          <Input
            aria-label="Find case"
            placeholder="Find case by C-number, customer, or subject"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search.trim() && searchQuery.data?.length ? (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
              {searchQuery.data.map((hit) => (
                <button
                  key={hit.id}
                  type="button"
                  className="block w-full cursor-pointer rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setCaseId(hit.id)
                    setCaseLabel(hit.label)
                    setSearch('')
                    setThreadId('')
                  }}
                >
                  {hit.label}
                </button>
              ))}
            </div>
          ) : null}
          {caseId ? <p className="mt-1 truncate text-xs text-muted-foreground">{caseLabel}</p> : null}
        </div>
        <Picker
          ariaLabel="Destination conversation"
          className="min-h-9 bg-background"
          value={threadId}
          disabled={!caseId || threadsQuery.isPending}
          clearable
          options={threads.map((thread) => ({
            value: thread.id,
            label: routeLabel(thread.audience, thread.display_name_internal),
          }))}
          placeholder={caseId ? 'Choose conversation' : 'Choose a case first'}
          onValueChange={setThreadId}
        />
        <Button
          disabled={!caseId || !threadId}
          pending={route.isPending}
          onClick={() => caseId && threadId && route.mutate({ inboundId: message.id, caseId, threadId })}
        >
          Route reply
        </Button>
      </div>
      {searchQuery.isError || threadsQuery.isError || route.isError ? (
        <p className="text-sm text-warning">{describeError(searchQuery.error || threadsQuery.error || route.error)}</p>
      ) : null}
    </article>
  )
}

function UnroutedInbox() {
  const query = useQuery(unroutedInboundQuery())
  if (query.isPending) return <SkeletonRows count={2} />
  if (query.isError) return <ErrorBanner title="Unrouted email is unreachable" detail={describeError(query.error)} />
  if (!query.data?.length) return null
  return (
    <Section title="Unrouted inbound" count={query.data.length} bodyClassName="space-y-3">
      <p className="text-sm text-muted-foreground">
        Choose the case and the conversation it belongs in. The message is kept safe while it waits.
      </p>
      {query.data.map((message) => (
        <UnroutedCard key={message.id} message={message} />
      ))}
    </Section>
  )
}

export function NotificationsScreen() {
  const me = useMe()
  const [category, setCategory] = React.useState<NotificationCategory | undefined>()
  const query = useQuery(notificationsFeedQuery({ category, includeSnoozed: true, limit: 200 }))
  const readAll = useReadAllNotifications()
  return (
    <Frame
      active="notifications"
      railless
      main={
        <div className="flex h-full flex-col overflow-y-auto">
          <PaneHeader title="Notifications" subtitle="Mentions, work that needs you, and FYIs in one self-resolving inbox." />
          <div className="mx-auto w-full max-w-4xl space-y-3 p-5">
            {me && ['manager', 'cover', 'billing'].includes(me.role) ? <UnroutedInbox /> : null}
            {me ? <CliqUnroutedInbox /> : null}
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((filter) => (
                <Button
                  key={filter.label}
                  size="sm"
                  variant={category === filter.value ? 'primary' : 'outline'}
                  onClick={() => setCategory(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
              <Button className="ml-auto" size="sm" variant="ghost" disabled={readAll.isPending} onClick={() => readAll.mutate(category)}>
                Mark all read
              </Button>
            </div>
            {query.isPending ? <SkeletonRows count={5} /> : null}
            {query.isError ? (
              <ErrorBanner title="Notifications unreachable" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
            ) : null}
            {query.data ? (
              <section className="overflow-hidden rounded-lg border border-border bg-card">
                {query.data.items.length ? (
                  <InboxList items={query.data.items} allowSnooze />
                ) : (
                  <p className="p-5 text-sm text-muted-foreground">No active notifications in this filter.</p>
                )}
              </section>
            ) : null}
          </div>
        </div>
      }
    />
  )
}
